import { put, call, takeLatest, type CallEffect, type PutEffect, type ForkEffect } from 'redux-saga/effects';
import { CustomerActionType } from './enums';
import { ProjectActionType } from '@store/branches/project/enums';
import { handleEmptyNullValues, setItems, toast, uploadDocumentArr } from '@utilities';
import {
	addQuotation,
	fetchQuotations,
	fetchUserDetails,
	sendOTP,
	updateQuotation,
	getPreDesignQuotation,
	getPrediction,
	getPredictionId,
	uploadFloorPlan,
	verifyOTP,
	getDefaultQuotations,
	fetchDocuments,
	fetchQuotationById,
	floorPlanValidator,
	fetchProductCategories,
	uploadProjectDetails,
	createQuotation,
	fetchTicketChat,
	sendMessage,
	closeTicket,
	fetch2DJson,
	fetch3DJson,
	fetchSavedQuotation,
	getCutlistData,
	getOptimiserData,
	getOptimizedCutlistData
} from './api';
import { activateDocument } from '../products/api';
import { AnyAction } from 'redux';
import { type CustomerAction } from './interfaces';
import { fetchQuotationRemarkCount } from './api';

type CustomerSagaEffect = Generator<CallEffect<any> | PutEffect<CustomerAction>>;
type CustomerSagaFormEffect = Generator<ForkEffect<void>>;

export function* updateFloorPlanEffect(action: AnyAction): any {
	const { formData, successCB, errorCB, finalCB } = action.payload;
	const normalizedProjectName = (formData?.project?.projectName ?? formData?.projectName ?? '').trim();
	if (!normalizedProjectName) {
		errorCB('project.projectName : must not be blank');
		finalCB();
		return;
	}

	const transformedData = {
		...formData,
		project: {
			...formData.project,
			projectName: normalizedProjectName
		}
	};
	const updateFloorPlanDocRequest = {
		...transformedData,
		attachments: formData?.files?.files.map((file: Record<string, any>) => ({
			file: file.name
		}))
	};

	try {
		const { data }: any = yield call(uploadFloorPlan, handleEmptyNullValues(updateFloorPlanDocRequest));
		const { attachments, category, subCategory, documentId } = data;
		const { arrPromises, uploadedDocuments } = uploadDocumentArr(
			{
				documents: [
					{
						category: formData.category,
						referenceType: formData.referenceType,
						subCategory: formData.subCategory,
						files: formData.files.files
					}
				]
			},
			[{ category, subCategory, attachments, documentId }]
		);
		yield call(async () => await Promise.all(arrPromises));
		if (uploadedDocuments.length > 0) {
			yield call(activateDocument, { documentIds: uploadedDocuments });
		}

		let chatGptData;
		while (true) {
			try {
				chatGptData = yield call(floorPlanValidator, { docId: uploadedDocuments[0] });
				if (chatGptData?.data && Object.keys(chatGptData.data).length > 0) {
					break;
				}
				chatGptData = yield call(floorPlanValidator, { docId: uploadedDocuments[0] });
			} catch (validatorError) {
				throw validatorError;
			}
		}
		const message = chatGptData?.data?.choices[0]?.message?.content;
		if (message.toLowerCase().includes('yes')) {
			if (successCB) successCB(data);
		} else {
			throw new Error('Upload a valid floor plan');
		}
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* sendCustomerOTPEffect(action: CustomerAction): any {
	const {
		leadDetails: { phoneNumber, name },
		successCB,
		errorCB
	} = action.payload;
	try {
		const data: any = yield call(sendOTP, { phone_number: phoneNumber, name });
		const payload = data.data;

		yield put({ type: CustomerActionType.SEND_CUSTOMER_OTP_SUCCESS, payload });
		successCB();
	} catch (err: unknown) {
		yield put({ type: CustomerActionType.SEND_CUSTOMER_OTP_FAILED });
		errorCB(err.message);
	}
}

export function* verifyCustomerOTPEffect(action: CustomerAction): any {
	const {
		sessionID,
		verifyOTPDetails: { otp },
		successCB,
		errorCB
	} = action.payload;
	try {
		const data: any = yield call(verifyOTP, {
			session_id: sessionID,
			otp
		});
		const payload = data.data;
		yield call(setItems, payload.auth_response);
		successCB();
	} catch (err: unknown) {
		yield put({ type: CustomerActionType.SEND_CUSTOMER_OTP_FAILED });
		errorCB(err.message);
	}
}

export function* resendCustomerOTPEffect(action: CustomerAction): any {
	try {
		const { phoneNumber } = action.payload;
		const data: any = yield call(sendOTP, {
			phone_number: `${phoneNumber}`
		});
		const payload = data.data;
		yield put({ type: CustomerActionType.SEND_CUSTOMER_OTP_SUCCESS, payload });
	} catch (err: unknown) {
		yield put({ type: CustomerActionType.SEND_CUSTOMER_OTP_FAILED });
		toast('ERROR', err.response?.data?.message);
	}
}

export function* fetchQuotationsEffect(action: CustomerAction): any {
	const { leadId, successCB, errorCB, finalCB } = action.payload;
	try {
		const { data }: any = yield call(fetchQuotations, { leadId, system: true });
		yield put({
			type: CustomerActionType.STORE_QUOTATIONS,
			payload: data
		});
		if (successCB) successCB(data);
	} catch (error) {
		if (errorCB) errorCB();
		yield put({
			type: CustomerActionType.ERROR_FETCH_QUOTATIONS,
			payload: {
				error
			}
		});
	} finally {
		finalCB();
	}
}

export function* addQuotationsEffect(action: CustomerAction): any {
	const { selectedPackages, leadId, total, projectId, successCB, errorCB } = action.payload;
	const transformedData = {
		leadId,
		projectId,
		total,
		areas: selectedPackages
	};
	try {
		const data = yield call(addQuotation, transformedData);
		successCB(data);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* fetchCustomerDetailsEffect(action: CustomerAction): any {
	try {
		const { successCB } = action.payload;
		const { data }: any = yield call(fetchUserDetails);
		yield put({
			type: CustomerActionType.STORE_CUSTOMER_DETAILS,
			payload: {
				...data
			}
		});
		successCB(data);
	} catch (error) {
		yield put({
			type: CustomerActionType.ERROR_CUSTOMER_DETAILS,
			payload: {
				error
			}
		});
	}
}
function transformQuotationRequest(modifiedData) {
	// Group products by areaName
	const groupedByArea = modifiedData.reduce((acc, product) => {
		const { areaName, areaEnum } = product;
		if (!acc[areaName]) {
			acc[areaName] = { areaEnum, areaName, products: [] };
		}
		acc[areaName].products.push(product);
		return acc;
	}, {});

	const areas = Object.keys(groupedByArea).map(areaNameKey => {
		const { areaEnum, areaName, products } = groupedByArea[areaNameKey];
		const transformedProducts = products.map(product => ({
			productName: product.productName,
			unitOfMeasure: product.unitOfMeasure,
			measurement: {
				width: product?.measurement?.width,
				depth: product?.measurement?.depth,
				height: product?.measurement?.height,
				area: product.measurement?.area
			},
			price: product.price,
			productId: product.productId,
			quantity: product.quantity,
			specification: product.specification,
			description: product.description,
			calculation: product.calculation,
			type: product.type,
			productSubCategory: product.productSubCategoryName,
			productCategory: product.productCategoryName,
			unitCost: product.unitCost,
			imageUrl: product.imageUrl
		}));

		const subTotal = transformedProducts.reduce((sum, product) => sum + product.price * product.quantity, 0);
		const packageName = transformedProducts[0].type;

		return {
			products: transformedProducts,
			subTotal,
			areaEnum,
			areaName,
			packageName
		};
	});

	return { areas };
}

// Helper function to revert area names back to original form
const revertAreaName = areaName => {
	// Remove _floor_X suffix if it exists
	const floorSuffixRegex = /_floor_\d+$/;
	return areaName.replace(floorSuffixRegex, '');
};

// Helper function to extract floor number from area key
const extractFloorNumber = areaKey => {
	const floorMatch = areaKey.match(/_floor_(\d+)$/);
	return floorMatch ? parseInt(floorMatch[1], 10) : 0; // Default to 0 (ground floor) if no floor suffix
};

const transformQuotationRequestCustomer = quotationData => {
	const transformedData = {
		total: quotationData.grandTotal,
		areas: []
	};

	const extractProducts = (groupsObj, intoArray, packageType) => {
		Object.keys(groupsObj || {}).forEach(groupKey => {
			const group = groupsObj[groupKey];

			if (Array.isArray(group)) {
				group.forEach(product => {
					if (product.isProductSelected && product.userSelection) {
						intoArray.push({
							productName: product.productName,
							unitOfMeasure: product.unitOfMeasure,
							measurement: {
								width: product.measurement?.width || 0,
								depth: product.measurement?.depth || 0,
								height: product.measurement?.height || 0,
								area: product.measurement?.area || 0
							},
							productId: product?.productId,
							price: product.price,
							unitCost: product.unitCost,
							quantity: product.quantity,
							specification: product.specification || '',
							description: product.description || '',
							calculation: product.calculation,
							type: packageType,
							productSubCategory: product.productSubCategory,
							productCategory: product.productCategory,
							imageUrl: product.imageUrl,
							active: true,
							warrantyCodes: product.warrantyCodes,
							builder: product.builder,
							paintSurfaces: product.paintSurfaces || []
						});
					}
				});
			} else if (group && typeof group === 'object') {
				Object.keys(group).forEach(subKey => {
					const subGroup = group[subKey];
					if (Array.isArray(subGroup)) {
						subGroup.forEach(product => {
							if (product.isProductSelected && product.userSelection) {
								intoArray.push({
									productName: product.productName,
									unitOfMeasure: product.unitOfMeasure,
									measurement: {
										width: product.measurement?.width || 0,
										depth: product.measurement?.depth || 0,
										height: product.measurement?.height || 0,
										area: product.measurement?.area || 0
									},
									productId: product.productId,
									price: product.price,
									unitCost: product.unitCost,
									quantity: product.quantity,
									specification: product.specification || '',
									description: product.description || '',
									calculation: product.calculation,
									type: packageType,
									productSubCategory: product.productSubCategory,
									productCategory: product.productCategory,
									imageUrl: product.imageUrl,
									active: true,
									warrantyCodes: product.warrantyCodes,
									builder: product.builder,
									paintSurfaces: product.paintSurfaces || []
								});
							}
						});
					}
				});
			}
		});
	};

	Object.keys(quotationData).forEach(areaKey => {
		const area = quotationData[areaKey];
		if (area && typeof area === 'object' && area.areaEnum) {
			const selectedKey = Object.keys(area).find(k => k.endsWith('Quotation') && area[k]?.isSelected);
			if (!selectedKey) return;

			const sel = area[selectedKey];
			const pkgType = selectedKey.replace('Quotation', '').toUpperCase();
			const products = [];

			// regular product groups
			extractProducts(sel.products, products, pkgType);

			// any xorGroup_* on the quotation itself
			Object.keys(sel)
				.filter(k => k.startsWith('xorGroup'))
				.forEach(xorKey => {
					extractProducts(sel[xorKey], products, pkgType);
				});

			// Revert area name to original form (remove _floor_X suffix) but keep each area as separate entry
			const originalAreaName = revertAreaName(area.areaName);
			// Extract floor number from the area key
			const floorNumber = extractFloorNumber(areaKey);

			products.length > 0 &&
				transformedData.areas.push({
					packageName: pkgType,
					products,
					subTotal: sel.areaTotal,
					areaEnum: area.areaEnum,
					areaName: originalAreaName,
					floor: floorNumber
				});
		}
	});

	return transformedData;
};

export function* updateQuotationEffect(action: CustomerAction): any {
	const { areas, successCB, errorCB, leadId, projectId } = action.payload;

	const transformedRequest = transformQuotationRequest(areas);
	const total = transformedRequest.areas.reduce((sum, area) => sum + area.subTotal, 0);

	try {
		const { data }: any = yield call(updateQuotation, { ...transformedRequest, total, projectId, leadId });
		const transformedData = {
			...data,
			areas: data?.areas?.map((area: Record<string, any>) => ({
				...area,
				areaName: area.name
			}))
		};

		successCB(transformedData);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* getPredictionIdEffect(action: CustomerAction): any {
	try {
		const { successCB, errorCB, documentId, projectId } = action.payload;
		const wallHeights = action.payload?.wall_heights ?? action.payload?.wallHeights;
		const payload: { documentId: any; projectId: any; wall_heights?: number[] } = {
			documentId,
			projectId
		};
		if (Array.isArray(wallHeights) && wallHeights.length > 0) {
			payload.wall_heights = wallHeights;
		}
		const { data }: any = yield call(getPredictionId, payload);
		yield put({
			type: CustomerActionType.STORE_PREDICTION_ID,
			payload: data
		});
		successCB(data);
	} catch (error: any) {
		yield put({
			type: CustomerActionType.ERROR_CUSTOMER_DETAILS,
			payload: {
				error
			}
		});
		if (action.payload?.errorCB) {
			action.payload.errorCB(error?.message ?? 'Failed to start prediction');
		}
	}
}

// export function* getPredictionEffect(action: CustomerAction): any {
// 	try {
// 		const { successCB, predictionId } = action.payload;
// 		const { data }: any = yield call(getPrediction, predictionId);
// 		if (data.status == 'succeeded') {
// 			yield put({
// 				type: CustomerActionType.STORE_AREAS,
// 				payload: data
// 			});
// 		}

// 		successCB(data);
// 	} catch (error) {
// 		yield put({
// 			type: CustomerActionType.ERROR_CUSTOMER_DETAILS,
// 			payload: {
// 				error
// 			}
// 		});
// 	}
// }
export function* getPredictionEffect(action: CustomerAction): any {
	const { successCB, errorCB, id } = action.payload;
	try {
		const { data }: any = yield call(getPrediction, id);

		if (data.status === 'succeeded') {
			yield put({
				type: CustomerActionType.STORE_AREAS,
				payload: data
			});
			successCB && successCB(data);
		} else if (data.status === 'rejected') {
			yield put({
				type: CustomerActionType.PREDICTION_FAILED,
				payload: { error: 'Prediction was rejected.' }
			});
			errorCB && errorCB('Prediction was rejected.');
		} else {
			successCB && successCB(data);
		}
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.PREDICTION_FAILED,
			payload: { error }
		});
		errorCB(error.message);
	}
}

export function* getPreDesignQuotationEffect(action: CustomerAction): any {
	const { successCB, selectedAreas, budget, errorCB, calculationId, savedProjectId, projectId } = action.payload;
	const effectiveProjectId = projectId ?? savedProjectId;
	const transformedData = selectedAreas.map(area => ({
		...area,
		productCategories: Object.entries(area.productCategories)
			.filter(([_, value]) => value)
			.map(([key]) => key)
	}));

	try {
		const { data }: any = yield call(getPreDesignQuotation, {
			areas: transformedData,
			budget: budget,
			calculationId,
			projectId: effectiveProjectId,
			saveToProject: effectiveProjectId
		});

		yield put({
			type: CustomerActionType.STORE_PRE_DESIGN_QUOTATION,
			payload: data
		});
		successCB(data);
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.ERROR_PRE_DESIGN_QUOTATION,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* getDefaultQuotationsEffect(action: CustomerAction): any {
	const { selectedAreas, budget, errorCB, successCB, calculationId, savedProjectId, projectId } =
		action.payload;
	const effectiveProjectId = projectId ?? savedProjectId;
	const normalizeProductCategories = (pc: any): string[] => {
		if (pc == null) return [];
		if (Array.isArray(pc)) return pc;
		if (typeof pc === 'object') {
			return Object.entries(pc)
				.filter(([, v]) => v)
				.map(([k]) => k);
		}
		return [];
	};
	const transformedData = selectedAreas.map(area => ({
		...area,
		productCategories: normalizeProductCategories(area.productCategories)
	}));
	try {
		const { data }: any = yield call(getDefaultQuotations, {
			areas: transformedData,
			budget: budget,
			calculationId,
			projectId: effectiveProjectId,
			saveToProject: effectiveProjectId
		});

		yield put({
			type: CustomerActionType.STORE_DEFAULT_QUOTATION,
			payload: data
		});
		if (successCB) {
			successCB(data);
		}
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.ERROR_DEFAULT_QUOTATION,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* fetchFloorPlanImageEffect(action: CustomerAction): any {
	const { documentIds, errorCB } = action.payload;

	try {
		const { data }: any = yield call(fetchDocuments, { documentIds: documentIds });
		yield put({
			type: CustomerActionType.STORE_FLOOR_PLAN_IMAGES,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.ERROR_FLOOR_PLAN_IMAGES,
			payload: {
				error
			}
		});
		errorCB(error?.message);
	}
}

export function* fetchQuotationByIdEffect(action: CustomerAction): any {
	const { quotationId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchQuotationById, quotationId);

		yield put({
			type: CustomerActionType.STORE_QUOTATION_BY_ID,
			payload: data
		});
		if (successCB) successCB(data);
	} catch (error) {
		yield put({
			type: CustomerActionType.ERROR_QUOTATION_BY_ID,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* fetchProductCategoriesEffect(action: AnyAction): any {
	const { successCB, selectedAreas, budget, errorCB, projectId, saveToProject } = action.payload;
	try {
		const { data }: any = yield call(fetchProductCategories, {
			areas: Array.isArray(selectedAreas) ? selectedAreas : selectedAreas?.areas,
			budget: budget,
			...(!!saveToProject ? { saveToProject } : { projectId })
		});

		const normalizedPayload = Array.isArray(data)
			? { areas: data }
			: data?.areas
				? data
				: {
						areas: data?.data?.areas || data?.data || [],
						calculationId: data?.calculationId || data?.data?.calculationId
					};

		yield put({
			type: ProjectActionType.STORE_PROJECT_PRODUCT_CATEGORIES,
			payload: normalizedPayload
		});
		yield put({
			type: CustomerActionType.STORE_PRODUCT_CATEGORIES_FLOOR_PLAN,
			payload: normalizedPayload
		});
		if (successCB) successCB(normalizedPayload);
	} catch (error: unknown) {
		yield put({
			type: ProjectActionType.ERROR_PROJECT_PRODUCT_CATEGORIES,
			payload: { error }
		});
		yield put({
			type: CustomerActionType.ERROR_FETCH_PRODUCT_CATEGORIES_FLOOR_PLAN,
			payload: { error }
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* uploadProjectDetailsEffect(action: AnyAction): any {
	const { formData, successCB, errorCB, finalCB } = action.payload;
	const normalizedProjectName = (formData?.project?.projectName ?? formData?.projectName ?? '').trim();
	if (!normalizedProjectName) {
		errorCB('project.projectName : must not be blank');
		finalCB();
		return;
	}

	const transformedData = {
		leadId: formData.leadId,
		budget: formData.budget,
		project: { ...formData.project, projectName: normalizedProjectName },
		documentId: formData.docId
	};
	try {
		const { data }: any = yield call(uploadProjectDetails, handleEmptyNullValues(transformedData));
		const { projectId, projectName, leadName } = data;
		yield put({
			type: CustomerActionType.SET_PROJECT_DETAILS,
			payload: { projectId, projectName, leadName, budget: formData?.budget }
		});
		successCB(transformedData?.documentId, projectId);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		finalCB();
	}
}

export function* createQuotationEffect(action: AnyAction): any {
	const { currentQuotation, predictionId, successCB, errorCB, leadId, projectId, calculationId, promoCode, erp } =
		action.payload;
	const transformedRequest = transformQuotationRequestCustomer(currentQuotation);
	yield put({ type: ProjectActionType.CREATE_QUOTATION_LOADING, payload: true });

	try {
		const { data }: any = yield call(createQuotation, {
			...transformedRequest,
			projectId,
			leadId,
			predictionId,
			promoCode,
			calculationId,
			erp
		});
		const transformedData = {
			...data,
			areas: data?.areas?.map((area: Record<string, any>) => ({
				...area,
				areaName: area.name
			}))
		};

		successCB(transformedData);
	} catch (error: unknown) {
		errorCB(error?.message);
	} finally {
		yield put({ type: ProjectActionType.CREATE_QUOTATION_LOADING, payload: false });
	}
}

export function* fetchCutlistDataEffect(action: AnyAction): any {
	const { quotationId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getCutlistData, quotationId);
		yield put({
			type: ProjectActionType.STORE_CUTLIST_DATA,
			payload: data
		});
		if (successCB) successCB(data);
	} catch (error: unknown) {
		yield put({
			type: ProjectActionType.ERROR_CUTLIST_DATA,
			payload: error
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchOptimiserDataEffect(action: AnyAction): any {
	const { cutlistData, successCB, errorCB, projectId } = action.payload;
	try {
		const { data }: any = yield call(getOptimiserData, cutlistData?.cutlist || cutlistData, projectId);
		yield put({
			type: ProjectActionType.STORE_OPTIMISER_DATA,
			payload: { data }
		});
		if (successCB) successCB();
	} catch (error: unknown) {
		yield put({
			type: ProjectActionType.ERROR_OPTIMISER_DATA,
			payload: error
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchOptimizedCutlistDataEffect(action: AnyAction): any {
	const { projectId, successCB, errorCB } = action.payload;
	try {
		const { data }: any = yield call(getOptimizedCutlistData, projectId);
		yield put({
			type: ProjectActionType.STORE_OPTIMISER_DATA,
			payload: { data }
		});
		if (successCB) successCB(data);
	} catch (error: unknown) {
		yield put({
			type: ProjectActionType.ERROR_OPTIMISER_DATA,
			payload: error
		});
		if (errorCB) errorCB(error?.message);
	}
}

export function* fetchTicketChatEffect(action: CustomerAction): any {
	const { ticketId, errorCB } = action.payload;
	try {
		const { data }: any = yield call(fetchTicketChat, ticketId);
		yield put({
			type: CustomerActionType.STORE_TICKET_CHAT,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.ERROR_FETCH_TICKET_CHAT,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB(error?.message);
		}
	}
}

export function* sendChatMessageEffect(action: CustomerAction): any {
	const { values, ticketId, successCB, errorCB, finalCB } = action.payload;
	const transformedData = {
		...values,
		attachments: values?.document
			? values?.document?.files?.map(file => {
					return { file: file.name, coverImage: false };
				})
			: []
	};
	delete transformedData.document;
	!transformedData.attachments.length && delete transformedData.attachments;

	try {
		const { data }: any = yield call(sendMessage, ticketId, transformedData);
		if (data) {
			const { attachments, category, subCategory, documentId } = data;
			const res = uploadDocumentArr({ documents: [values?.document] }, [
				{ category, subCategory, attachments, documentId }
			]);
			const { arrPromises, uploadedDocuments } = res;
			yield call(async () => await Promise.all(arrPromises));

			if (uploadedDocuments.length > 0) {
				yield call(activateDocument, { documentIds: uploadedDocuments });
			}
		}

		if (successCB) successCB(data);
	} catch (error: unknown) {
		if (errorCB) {
			errorCB(error?.message);
		}
	} finally {
		finalCB();
	}
}

export function* closeTicketEffect(action: CustomerAction): any {
	const { successCB, errorCB, ticketId, finalCB } = action.payload;
	try {
		yield call(closeTicket, ticketId);
		successCB();
	} catch (error) {
		errorCB();
	} finally {
		finalCB();
	}
}

export function* fetchTwoDJsonEffect(action: AnyAction): any {
	const { predictionId, errorCB, currentFloor } = action.payload;
	try {
		const { data }: any = yield call(fetch2DJson, predictionId, currentFloor);

		yield put({
			type: CustomerActionType.STORE_2D_JSON,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.ERROR_FETCH_2D_JSON,
			payload: { error }
		});

		errorCB(error?.message);
	}
}

export function* fetchThreeDJsonEffect(action: AnyAction): any {
	const { predictionId, errorCB, currentFloor } = action.payload;

	try {
		const { data }: any = yield call(fetch3DJson, predictionId, currentFloor);

		yield put({
			type: CustomerActionType.STORE_3D_JSON,
			payload: data
		});
	} catch (error: unknown) {
		yield put({
			type: CustomerActionType.ERROR_FETCH_3D_JSON,
			payload: { error }
		});
		errorCB(error?.message);
	}
}

export function* fetchSavedQuotationEffect(action: any) {
	const { errorCB, successCB, predictionId } = action.payload;
	try {
		const { data }: any = yield call(fetchSavedQuotation, predictionId);
		yield put({ type: CustomerActionType.STORE_AREAS, payload: data });
		if (successCB) successCB(data);
	} catch (error: unknown) {
		errorCB(error?.message);
	}
}

export function* updateFloorPlanSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.UPLOAD_FLOOR_PLAN, updateFloorPlanEffect);
}

export function* sendCustomerOTPSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.SEND_CUSTOMER_OTP, sendCustomerOTPEffect);
}

export function* verifyCustomerOTPSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.VERIFY_CUSTOMER_OTP, verifyCustomerOTPEffect);
}

export function* resendCustomerOTPSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.RESEND_CUSTOMER_OTP, resendCustomerOTPEffect);
}

export function* fetchQuotationsSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_QUOTATIONS, fetchQuotationsEffect);
}

export function* addQuotationsSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.ADD_QUOTATION, addQuotationsEffect);
}

export function* fetchCustomerDetailsSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_CUSTOMER_DETAILS, fetchCustomerDetailsEffect);
}

export function* updateQuotationSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.UPDATE_QUOTATION, updateQuotationEffect);
}

export function* getPredictionIdSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.GET_PREDICTION_ID, getPredictionIdEffect);
}

export function* getPredictionSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.GET_PREDICTION, getPredictionEffect);
}

export function* getPreDesignQuotationSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.GET_PRE_DESIGN_QUOTATION, getPreDesignQuotationEffect);
}

export function* getDefaultQuotationsSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.GET_DEFAULT_QUOTATION, getDefaultQuotationsEffect);
}
export function* fetchFloorPlanImageSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_FLOOR_PLAN_IMAGES, fetchFloorPlanImageEffect);
}

export function* fetchQuotationByIdSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_QUOTATION_BY_ID, fetchQuotationByIdEffect);
}

export function* fetchProductCategoriesSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_PRODUCT_CATEGORIES_FLOOR_PLAN, fetchProductCategoriesEffect);
	yield takeLatest(ProjectActionType.FETCH_PROJECT_PRODUCT_CATEGORIES, fetchProductCategoriesEffect);
}

export function* uploadProjectDetailsSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.UPLOAD_PROJECT_DETAILS, uploadProjectDetailsEffect);
}

export function* createQuotationSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.CREATE_QUOTATION, createQuotationEffect);
}

export function* fetchTicketChatSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_TICKET_CHAT, fetchTicketChatEffect);
}

export function* sendChatMessageSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.SEND_CHAT_MESSAGE, sendChatMessageEffect);
}

export function* closeTicketSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.CLOSE_TICKET, closeTicketEffect);
}

export function* fetchTwoDJsonSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_2D_JSON, fetchTwoDJsonEffect);
}

export function* fetchThreeDJsonSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_3D_JSON, fetchThreeDJsonEffect);
}

export function* fetchSavedQuotationSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_SAVED_QUOTATION, fetchSavedQuotationEffect);
}

export function* fetchCutlistDataSaga(): CustomerSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_CUTLIST_DATA, fetchCutlistDataEffect);
}

export function* fetchOptimiserDataSaga(): CustomerSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_OPTIMISER_DATA, fetchOptimiserDataEffect);
}

export function* fetchOptimizedCutlistDataSaga(): CustomerSagaFormEffect {
	yield takeLatest(ProjectActionType.FETCH_OPTIMIZED_CUTLIST_DATA, fetchOptimizedCutlistDataEffect);
}

/** Normalize productId for map keys: no spaces, underscores, or dashes; lowercase */
function normalizeRemarkCountKey(productId: string): string {
	return String(productId ?? '')
		.replace(/[\s_\-]/g, '')
		.toLowerCase();
}

export function* fetchQuotationRemarkCountEffect(action: AnyAction): any {
	const { quotationId, successCB, errorCB } = action.payload;
	try {
		const res: any = yield call(fetchQuotationRemarkCount, quotationId);
		const data = res?.data ?? res;
		const counts =
			data?.remarkCount ??
			data?.remark_count ??
			data?.data?.remarkCount ??
			data?.data?.remark_count ??
			data?.counts ??
			data?.data?.counts ??
			(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : undefined);
		// Store with normalized keys (no space/underscore/dash, lowercase) for consistent lookup
		const map: Record<string, number> = {};
		if (Array.isArray(counts)) {
			for (const item of counts) {
				const key = item?.productId ?? item?.product_id ?? item?.id;
				const value = Number(item?.remarkCount ?? item?.remark_count ?? item?.count ?? item?.total);
				if (key !== undefined && key !== null && Number.isFinite(value)) map[normalizeRemarkCountKey(String(key))] = value;
			}
		} else if (counts && typeof counts === 'object') {
			for (const [key, rawValue] of Object.entries(counts)) {
				const value = Number(rawValue && typeof rawValue === 'object'
					? (rawValue as any).remarkCount ?? (rawValue as any).remark_count ?? (rawValue as any).count ?? (rawValue as any).total
					: rawValue);
				if (Number.isFinite(value)) map[normalizeRemarkCountKey(key)] = value;
			}
		}
		yield put({
			type: CustomerActionType.STORE_QUOTATION_REMARK_COUNT,
			payload: map
		});
		if (successCB) successCB(map);
	} catch (error) {
		yield put({
			type: CustomerActionType.ERROR_QUOTATION_REMARK_COUNT,
			payload: {
				error
			}
		});
		if (errorCB) {
			errorCB((error as any)?.message);
		}
	}
}

export function* fetchQuotationRemarkCountSaga(): CustomerSagaFormEffect {
	yield takeLatest(CustomerActionType.FETCH_QUOTATION_REMARK_COUNT, fetchQuotationRemarkCountEffect);
}
